import AsyncSelect from 'react-select/async';
import { useState, useRef, useCallback } from 'react';
import { components } from 'react-select';
import { Tooltip } from '@gpstrackit/react-controls';
//Iconst
import SearchIcon from '@mui/icons-material/Search';
import { v4 as uuidv4 } from 'uuid';
import cx from 'classnames';
//

//Styles
import './searchInput.scss';
import { searchDataIS } from 'Core/common/redux/infiniteScrollSlice';

// REQ-8.2: Debounce constant for autocomplete dropdown fetching
// Separate from main search debounce (500ms) - autocomplete uses 300ms for snappier UX
const AUTOCOMPLETE_DEBOUNCE_MS = 300;

const Search = (props) => {
    const {
        graphqlFunction,
        graphqlConditions,
        placeholder,
        searchConditions,
        searchOptionValue,
        searchOptionLabel,
        value,
        onChange,
        customSearch,
        extraField,
        noSplit,
        splitNoDevices,
        checked,
        messages,
        isDisabled,
        style,
        noSearchOptions,
        openSearchCustomOptions,
        onlyEnterSearch,
        clearGridOnEmpty
    } = props;
    // REQ-8.3: Extract limit prop with default of 30 for backward compatibility
    const { limit = 30 } = props;
    const searchInput = useRef(null);
    // REQ-8.2: Ref to track debounce timer for autocomplete
    const debounceTimerRef = useRef(null);

    const [inputValue, setInputValue] = useState('');
    // REQ-8.2: Internal function that performs the actual search API call
    const performSearch = useCallback((inputValue, callback) => {
        if (noSplit || !checked) {
            if (!inputValue) {
                callback([]);
                return;
            }
            if (!noSearchOptions) {
                // REQ-8.3: Call searchData with object params for better readability and type safety
                searchDataIS({
                    query: graphqlFunction,
                    graphqlConditions,
                    conditions: searchConditions,
                    id: searchOptionValue,
                    label: searchOptionLabel,
                    value: inputValue,
                    customCondition: customSearch,
                    extraField,
                    limit,
                }).then((response) => {
                    if (response?.result) {
                        const { items } = response?.result;
                        callback(items);
                    } else {
                        callback([]);
                    }
                });
            } else {
                callback([]);
            }
        } else {
            callback([]);
        }
    }, [noSplit, checked, noSearchOptions, graphqlFunction, graphqlConditions, searchConditions, searchOptionValue, searchOptionLabel, customSearch, extraField, limit]);

    // REQ-8.2: Debounced wrapper for loadOptions - prevents excessive API calls on every keystroke
    // Uses 300ms debounce for autocomplete (separate from main search 500ms debounce)
    const loadOptions = useCallback((inputValue, callback) => {
        // Clear any pending debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Handle empty input immediately (no need to debounce)
        if (!inputValue) {
            callback([]);
            return;
        }

        // REQ-8.2: Debounce the search API call
        debounceTimerRef.current = setTimeout(() => {
            performSearch(inputValue, callback);
        }, AUTOCOMPLETE_DEBOUNCE_MS);
    }, [performSearch]);

    const handleInputChange = (newValue) => {
        const inputValue = newValue
        setInputValue(inputValue);
        return inputValue;
    };

    const DropdownIndicator = (props) => {
        return (
            <components.DropdownIndicator {...props}>
                <SearchIcon onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange([...value], null, "autoSearch")
                }} />
            </components.DropdownIndicator>
        );
    };

    const MultiValue = (props) => {
        const { children } = props;

        const editHandler = (item) => {
            // FIX: Clone array before mutating to avoid "Cannot delete property" error on frozen state
            let currentValue = [...(value || [])];
            let currentItem = item?.index;
            currentValue.splice(currentItem, 1);
            onChange(currentValue, true);
            searchInput?.current?.props?.onInputChange(item?.children.trim());
            let messageBody = document.querySelector('.react-select-search__value-container');
            messageBody.scrollTop = messageBody.scrollHeight - messageBody.clientHeight;
        }

        return (
            <components.MultiValue {...props}>
                <Tooltip title={`Edit ${children}`}>
                    <div
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            editHandler(props)
                        }}
                    >
                        <span>{children}</span>
                    </div>
                </Tooltip>
            </components.MultiValue>
        );
    }

    const codes = ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"]

    return (
        <div className={cx("search-input-container", (noSearchOptions && "search-input-container-hidden"))}>
            <AsyncSelect
                ref={searchInput}
                value={value}
                inputValue={inputValue}
                classNamePrefix="react-select-search"
                components={{ DropdownIndicator, MultiValue }}
                placeholder={placeholder || "Search"}
                loadOptions={loadOptions}
                onInputChange={(value) => handleInputChange(value)}
                isMulti
                isDisabled={isDisabled}
                getOptionLabel={e => {
                    if (openSearchCustomOptions) {
                        return openSearchCustomOptions(e)
                    } else {
                        if (extraField) {
                            return `${e[searchOptionLabel] || ''}  ${e?.company?.name ? `(${e?.company?.name})` : ''}`
                        } else {
                            return e[searchOptionLabel]
                        }
                    }
                }}
                getOptionValue={e => e[searchOptionValue]}
                isClearable
                cacheOptions
                defaultOptions
                onKeyDown={(e) => {
                    if (codes.includes(e.code)) {
                        e.preventDefault();
                    }
                    if ((e.code === "Enter" || e.code === "NumpadEnter") && inputValue && !checked) {
                        e.preventDefault();
                        // FIX: Clone array before mutating to avoid "Cannot add property" error on frozen state
                        let currentitems = [...(value || [])];
                        let newItems = { [searchOptionValue]: inputValue.trim(), [searchOptionLabel]: inputValue }
                        currentitems.push(newItems);
                        let hash = {};
                        currentitems = currentitems.filter(o => hash[o[searchOptionValue]] ? false : hash[o[searchOptionValue]] = true);
                        // onlyEnterSearch: add chip only, search fires on second Enter or search button
                        // default: add chip and fire search immediately
                        onChange(currentitems, onlyEnterSearch ? true : null, e.code);
                        setInputValue('');
                        searchInput?.current?.props?.onMenuClose();
                    }
                    if ((e.code === "Enter" || e.code === "NumpadEnter") && inputValue && checked) {
                        e.preventDefault();
                        // FIX: Clone array before mutating to avoid "Cannot add property" error on frozen state
                        let currentitems = [...(value || [])];
                        let newTags = splitNoDevices ? inputValue.split(/[,\r\n\t]+/).filter(element => element): inputValue.split(/[, ]+/).filter(element => element);
                        newTags.forEach((item) => {
                            currentitems.push({ [searchOptionValue]: `${item.trim()}-${uuidv4()}`, [searchOptionLabel]: item })
                        })
                        // onlyEnterSearch: add chips only, search fires on second Enter or search button
                        // default: add chips and fire search immediately
                        onChange(currentitems, onlyEnterSearch ? true : null, e.code);
                        setInputValue('');
                        searchInput?.current?.props?.onMenuClose();
                    }

                    if ((e.code === "Enter" || e.code === "NumpadEnter") && !inputValue && value.length > 0 && onlyEnterSearch) {
                        // Second Enter with no text (only when onlyEnterSearch): trigger the global search
                        onChange([...value], null, e.code);
                        setInputValue('');
                        searchInput?.current?.props?.onMenuClose();
                    }
                }}
                onChange={(e, actionMeta) => {
                    if (actionMeta.action === "remove-value") {
                        let removedValue = actionMeta.removedValue
                        // FIX: Clone array before mutating to avoid "Cannot delete property" error on frozen state
                        let currentValue = [...(value || [])];
                        let deleteItem = currentValue?.findIndex((item) => item[searchOptionValue] === removedValue[searchOptionValue]);
                        currentValue.splice(deleteItem, 1);
                        if (clearGridOnEmpty && currentValue.length === 0) {
                            // clearGridOnEmpty: last tag removed clears the grid without searching
                            onChange(currentValue, 'clearGrid', actionMeta.action);
                        } else {
                            // onlyEnterSearch: removing a tag never auto-searches
                            onChange(currentValue, onlyEnterSearch ? true : null, actionMeta.action);
                        }
                    } else if (actionMeta.action === "clear" && clearGridOnEmpty) {
                        // clearGridOnEmpty: clearing all chips clears the grid without searching
                        onChange([], 'clearGrid', actionMeta.action);
                    } else {
                        // select-option (picking from autocomplete): respect onlyEnterSearch
                        onChange(e, onlyEnterSearch ? true : null);
                    }
                }}
                onBlur={(e) => {
                    if (e.type == "blur" && inputValue?.length > 0) {
                        if (!checked) {
                            e.preventDefault();
                            // FIX: Clone array before mutating to avoid "Cannot add property" error on frozen state
                            let currentitems = [...(value || [])];
                            let newItems = { [searchOptionValue]: inputValue.trim(), [searchOptionLabel]: inputValue }
                            currentitems.push(newItems);
                            let hash = {};
                            currentitems = currentitems.filter(o => hash[o[searchOptionValue]] ? false : hash[o[searchOptionValue]] = true);
                            // onlyEnterSearch: blur only adds chip (search requires explicit Enter/button)
                            // default: blur adds chip and fires search
                            onChange(currentitems, onlyEnterSearch ? true : null);
                            setInputValue('');
                            searchInput?.current?.props?.onMenuClose();
                        } else {
                            e.preventDefault();
                            // FIX: Clone array before mutating to avoid "Cannot add property" error on frozen state
                            let currentitems = [...(value || [])];
                            let newTags = inputValue.split(/[, ]+/).filter(element => element);
                            newTags.forEach((item) => {
                                currentitems.push({ [searchOptionValue]: `${item.trim()}-${uuidv4()}`, [searchOptionLabel]: item })
                            })
                            // onlyEnterSearch: blur only adds chips (search requires explicit Enter/button)
                            // default: blur adds chips and fires search
                            onChange(currentitems, onlyEnterSearch ? true : null);
                            setInputValue('');
                            searchInput?.current?.props?.onMenuClose();
                        }
                    }
                }}
                noOptionsMessage={() => messages['no_options'] || 'No Options'}
            />
        </div>
    );
}
export default Search;